var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Fields that differ between ANY two records, so marking them would only bury the
 * differences that mean something: control number, its agency, the last-change
 * timestamp, the cataloguing source. `008` joins them because the record being
 * written has none yet — it is derived at save — so it would be "only on the
 * candidate" every single time.
 */
export var ALWAYS_DIFFERENT_TAGS = new Set(['001', '003', '005', '008', '040']);
var normIndicator = function (ind) { return (ind !== null && ind !== void 0 ? ind : '').trim(); };
var subfieldKey = function (s) { var _a, _b; return "".concat((_a = s.subfieldCode) !== null && _a !== void 0 ? _a : '', "\0").concat((_b = s.value) !== null && _b !== void 0 ? _b : ''); };
/** Content only: indicators and the subfield sequence, never indexes or link ids. */
var signature = function (f) { return __spreadArray([
    normIndicator(f.indicator1),
    normIndicator(f.indicator2)
], f.subfields.map(subfieldKey), true).join('\u0001'); };
var overlap = function (a, b) {
    var keys = new Set(b.subfields.map(subfieldKey));
    return a.subfields.filter(function (s) { return keys.has(subfieldKey(s)); }).length;
};
var compareTags = function (a, b) { return (a < b ? -1 : a > b ? 1 : 0); };
var groupByTag = function (fields) {
    var _a;
    var byTag = new Map();
    for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
        var f = fields_1[_i];
        var list = (_a = byTag.get(f.fieldTag)) !== null && _a !== void 0 ? _a : [];
        list.push(f);
        byTag.set(f.fieldTag, list);
    }
    return byTag;
};
var compareTag = function (tag, left, right) {
    var _a, _b;
    if (ALWAYS_DIFFERENT_TAGS.has(tag)) {
        var rows_1 = [];
        for (var i = 0; i < Math.max(left.length, right.length); i++) {
            rows_1.push({ tag: tag, status: 'ignored', left: (_a = left[i]) !== null && _a !== void 0 ? _a : null, right: (_b = right[i]) !== null && _b !== void 0 ? _b : null });
        }
        return rows_1;
    }
    // Row order follows the left side — the record she is writing — with the
    // candidate's unmatched fields after it.
    var rows = [];
    var rightLeft = __spreadArray([], right, true);
    var pending = [];
    var _loop_1 = function (l) {
        var sig = signature(l);
        var at = rightLeft.findIndex(function (r) { return signature(r) === sig; });
        if (at >= 0) {
            rows.push({ tag: tag, status: 'same', left: l, right: rightLeft[at] });
            rightLeft.splice(at, 1);
        }
        else {
            rows.push({ tag: tag, status: 'leftOnly', left: l, right: null });
            pending.push(l);
        }
    };
    for (var _i = 0, left_1 = left; _i < left_1.length; _i++) {
        var l = left_1[_i];
        _loop_1(l);
    }
    // Best overlaps first, across all leftovers at once: a greedy walk in left order
    // would let an early field take the partner a later one shares more with.
    var candidates = [];
    for (var _c = 0, pending_1 = pending; _c < pending_1.length; _c++) {
        var l = pending_1[_c];
        for (var _d = 0, rightLeft_1 = rightLeft; _d < rightLeft_1.length; _d++) {
            var r = rightLeft_1[_d];
            var score = overlap(l, r);
            if (score > 0)
                candidates.push({ l: l, r: r, score: score });
        }
    }
    if (candidates.length === 0 && pending.length === 1 && rightLeft.length === 1) {
        candidates.push({ l: pending[0], r: rightLeft[0], score: 0 });
    }
    candidates.sort(function (a, b) { return b.score - a.score; });
    var pairedLeft = new Set();
    var pairedRight = new Set();
    var _loop_2 = function (l, r) {
        if (!pairedLeft.has(l) && !pairedRight.has(r)) {
            pairedLeft.add(l);
            pairedRight.add(r);
            var row = rows.find(function (x) { return x.left === l; });
            row.status = 'different';
            row.right = r;
        }
    };
    for (var _e = 0, candidates_1 = candidates; _e < candidates_1.length; _e++) {
        var _f = candidates_1[_e], l = _f.l, r = _f.r;
        _loop_2(l, r);
    }
    for (var _g = 0, _h = rightLeft.filter(function (x) { return !pairedRight.has(x); }); _g < _h.length; _g++) {
        var r = _h[_g];
        rows.push({ tag: tag, status: 'rightOnly', left: null, right: r });
    }
    return rows;
};
/**
 * @param left  the record being written (form-derived, may be unsaved)
 * @param right the candidate it might duplicate
 * @returns one row per field, ordered by tag
 */
export var compareRecords = function (left, right) {
    var leftByTag = groupByTag(left);
    var rightByTag = groupByTag(right);
    var tags = Array.from(new Set(Array.from(leftByTag.keys()).concat(Array.from(rightByTag.keys())))).sort(compareTags);
    return tags.flatMap(function (tag) { var _a, _b; return compareTag(tag, (_a = leftByTag.get(tag)) !== null && _a !== void 0 ? _a : [], (_b = rightByTag.get(tag)) !== null && _b !== void 0 ? _b : []); });
};
/** Subfields of `field` that the counterpart does not carry with the same value. */
export var unmatchedSubfields = function (field, counterpart) {
    if (!counterpart)
        return new Set(field.subfields);
    var keys = new Set(counterpart.subfields.map(subfieldKey));
    return new Set(field.subfields.filter(function (s) { return !keys.has(subfieldKey(s)); }));
};
